import logger from '@service/logger';

export class Events {
  static raiseZipCodeModal = 'modal.zipcode.show';
  static lowerZipCodeModal = 'modal.zipcode.hide';
  static zipCodeCollected = 'zipcode.collected';
  static zipCodeResolved = 'zipcode.resolved';
  static branchDatabaseReady = 'database.branch.ready';
  static branchZipCodeCollected = 'branch.zipcode.collected';
  static zipcodeDatabaseReady = 'database.zipcode.ready';
  static branchLocationResolved = 'branch.location.resolved';
  static cartIdResolved = 'cart.id.resolved';
  static message = 'message';
}

/**
 * Modern ES6 Pub/Sub (Publish-Subscribe) Pattern
 * Provides decoupled communication between theme components
 */
class PubSub {
  /** @type {Map<string, Function[]>} */
  topics = new Map();

  constructor() {}

  sub(topic, listener) {
    return this.subscribe(topic, listener);
  }

  pub(topic, info) {
    return this.publish(topic, info);
  }

  /**
   * Subscribe to a topic
   * @param {string} topic - The topic name to subscribe to
   * @param {Function} listener - The callback function
   * @returns {Object} Subscription object with remove method
   */
  subscribe(topic, listener) {
    if (typeof topic !== 'string' || typeof listener !== 'function') {
      throw new Error('Invalid arguments: topic must be string, listener must be function');
    }

    this.topics = this.topics || new Map();

    // Create the topic's array if not yet created
    if (!this.topics.has(topic)) {
      this.topics.set(topic, []);
    }

    // Add the listener to queue
    const listeners = this.topics.get(topic);
    const index = listeners.push(listener) - 1;

    // Return subscription object with remove method
    return {
      remove: () => {
        const currentListeners = this.topics.get(topic);
        if (currentListeners) {
          currentListeners.splice(index, 1);
          // Clean up empty topics
          if (currentListeners.length === 0) {
            this.topics.delete(topic);
          }
        }
      },
    };
  }

  /**
   * Publish data to all subscribers of a topic
   * @param {string} topic - The topic name to publish to
   * @param {*} info - The data to send to subscribers
   */
  publish(topic, info = {}) {
    // If the topic doesn't exist, or there are no listeners, register the topic
    // so others can pick up on it later.

    if (!this.topics.has(topic)) {
      logger.warn(`No topic registered: "${topic}".`);
      logger.info(`Registering topic: "${topic}".`);
      this.topics.set(topic, []);
      this.publish(topic, info);
    }

    const listeners = this.topics.get(topic);

    // Create a copy of listeners to avoid issues if listeners are removed during iteration
    [...listeners].forEach(listener => {
      try {
        listener(info);
        logger.debug(`PubSub listener registered for topic "${topic}".`);
      } catch (error) {
        logger.error(`Error in PubSub listener for topic "${topic}":`, error);
      }
    });
  }

  /**
   * Remove all subscribers from a topic
   * @param {string} topic - The topic to clear
   */
  clear(topic) {
    if (topic) {
      this.topics.delete(topic);
    } else {
      this.topics.clear();
    }
  }

  /**
   * Get the number of subscribers for a topic
   * @param {string} topic - The topic name
   * @returns {number} Number of subscribers
   */
  getSubscriberCount(topic) {
    return this.topics.has(topic) ? this.topics.get(topic).length : 0;
  }

  /**
   * Get all active topic names
   * @returns {string[]} Array of topic names
   */
  getTopics() {
    return [...this.topics.keys()];
  }
}

// This class will be registered as a singleton
export const pubsub = new PubSub();
